import { useMutation, useQuery } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { BaseLoader } from '@retainerclub/common';
import {
  CREATE_CART_TRANSACTION,
  CREATE_ORDER,
  CREATE_SUBSCRIPTION_ACKNOWLEDGMENT,
  GET_ACTIVE_CHECKOUT_ATTEMPT,
} from '@retainerclub/customer-api';
import {
  useAccount,
  useAuth,
  useCart,
  useCheckoutTaxQuote,
} from '@retainerclub/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';
import {
  disclosedCartItems,
  SubscriptionDisclosureModal,
} from '../SubscriptionDisclosure';
import CheckoutCart from './CheckoutCart/CheckoutCart';
import CheckoutFitConfirmationModal from './CheckoutFitConfirmationModal';
import CheckoutForm from './CheckoutForm/CheckoutForm';
import {
  canFinishPaidQuotedAttempt,
  createCheckoutIdempotencyKey,
  initialValues,
  recoveredCheckoutAttempt,
  requiresFitConfirmation,
  schema,
} from './helpers';

const PRE_PAYMENT_REJECTION_CODES = new Set([
  'CART_NOT_FOUND',
  'CART_OWNERSHIP_FAILURE',
  'EXPIRED_TAX_QUOTE',
  'INVALID_DELIVERY_METHOD',
  'INVALID_IDEMPOTENCY_KEY',
  'INVALID_PAYMENT_CARD',
  'INVALID_TAX_ADDRESS',
  'PAYMENT_TRANSACTION_OPEN',
  'STALE_TAX_QUOTE',
  'SUBSCRIPTION_ACKNOWLEDGMENT_REQUIRED',
  'TAX_QUOTE_OWNERSHIP_FAILURE',
  'TAX_QUOTE_REQUIRED',
  'UNAUTHENTICATED',
  'UNSUPPORTED_PAYMENT_GATEWAY',
]);
const SUBSCRIPTION_ACKNOWLEDGMENT_CODE = 'SUBSCRIPTION_ACKNOWLEDGMENT_REQUIRED';
const REFRESHABLE_QUOTE_REJECTION_CODES = new Set([
  'EXPIRED_TAX_QUOTE',
  'STALE_TAX_QUOTE',
]);

function reconciliationErrors(message) {
  return [
    {
      path: 'payment',
      message:
        message ||
        'We cannot confirm whether your payment completed. Do not place the order again. Contact Retainer Club support for help.',
    },
  ];
}

function checkoutBlockingErrors({
  activeAttemptError,
  activeAttemptLoading,
  deliveryUpdateState,
  hasCartItems,
  retainedQuotedAttempt,
  requiresPaymentReconciliation,
  taxQuote,
}) {
  if (requiresPaymentReconciliation) {
    return reconciliationErrors(retainedQuotedAttempt?.message);
  }

  if (!(hasCartItems || canFinishPaidQuotedAttempt(retainedQuotedAttempt))) {
    return [
      {
        path: 'cart',
        message:
          'Your cart is empty. Please add an item before placing an order.',
      },
    ];
  }

  if (activeAttemptLoading && !retainedQuotedAttempt) {
    return [
      {
        path: 'payment',
        message:
          'Wait while we check for an existing payment attempt before placing the order.',
      },
    ];
  }

  if (activeAttemptError && !retainedQuotedAttempt) {
    return [
      {
        path: 'payment',
        message:
          'We could not check for an existing payment attempt. Retry payment status before placing the order.',
      },
    ];
  }

  if (retainedQuotedAttempt?.missingQuotedEvidence) {
    return [
      {
        path: 'payment',
        message:
          'The retained tax quote details could not be loaded. Contact Retainer Club support and do not place the order again.',
      },
    ];
  }

  if (retainedQuotedAttempt?.orderId) {
    return [
      {
        path: 'order',
        message: 'Your completed order is loading.',
      },
    ];
  }

  if (retainedQuotedAttempt?.status === 'pending') {
    return [
      {
        path: 'payment',
        message:
          'Your payment is still processing. Check payment status before continuing.',
      },
    ];
  }

  if (
    retainedQuotedAttempt?.status === 'ambiguous' &&
    retainedQuotedAttempt?.recoveryMode === 'legacy'
  ) {
    return [
      {
        path: 'payment',
        message:
          'We could not confirm the legacy payment result. Check payment status before continuing.',
      },
    ];
  }

  if (deliveryUpdateState.blocked) {
    return [
      {
        path: 'deliveryMethod',
        message: deliveryUpdateState.loading
          ? 'Wait for the delivery method update to finish.'
          : 'Confirm your saved delivery method before placing your order.',
      },
    ];
  }

  if (!(retainedQuotedAttempt || taxQuote.isReady)) {
    return [
      {
        path: 'tax',
        message:
          'Wait for tax calculation to finish before placing your order.',
      },
    ];
  }

  return null;
}

function prepareQuotedCheckoutInput({
  deliveryMethod,
  idempotencyKeyRef,
  quotedCheckoutInputRef,
  shippingAddress,
  setRetainedQuotedAttempt,
  taxCheckoutEnabled,
  taxQuoteId,
}) {
  if (quotedCheckoutInputRef.current) {
    return quotedCheckoutInputRef.current;
  }

  if (taxCheckoutEnabled !== true) {
    return null;
  }

  if (!idempotencyKeyRef.current) {
    idempotencyKeyRef.current = createCheckoutIdempotencyKey();
  }

  quotedCheckoutInputRef.current = {
    cartTransactionId: null,
    deliveryMethod,
    idempotencyKey: idempotencyKeyRef.current,
    legacyValuesFrozen: true,
    missingQuotedEvidence: false,
    recovered: false,
    recoveryMode: 'quoted',
    shippingAddress,
    status: 'submitted',
    taxQuoteId,
  };
  setRetainedQuotedAttempt(quotedCheckoutInputRef.current);

  return quotedCheckoutInputRef.current;
}

function provenPrePaymentRejection(errors) {
  return (
    errors.length > 0 &&
    errors.every((errorMessage) =>
      PRE_PAYMENT_REJECTION_CODES.has(errorMessage.code),
    )
  );
}

function refreshableQuoteRejection(errors) {
  return errors.some((errorMessage) =>
    REFRESHABLE_QUOTE_REJECTION_CODES.has(errorMessage.code),
  );
}

function classifyTransactionResponse(transactionData) {
  const payload = transactionData?.createCartTransaction;
  const transaction = payload?.cartTransaction;
  const errors = payload?.errorMessages || [];

  if (
    transaction?.state === 'reconciliation_required' ||
    errors.some(
      (errorMessage) => errorMessage.code === 'PAYMENT_RECONCILIATION_REQUIRED',
    )
  ) {
    return { kind: 'reconciliation', transaction };
  }

  if (
    transaction?.state === 'successful' &&
    errors.length > 0 &&
    errors.every(
      (errorMessage) =>
        errorMessage.code === 'PAYMENT_ATTEMPT_ALREADY_IN_PROGRESS',
    )
  ) {
    return { kind: 'successful', transaction };
  }

  if (errors.length > 0) {
    const releaseAttempt = !transaction && provenPrePaymentRejection(errors);
    return {
      errors,
      kind: 'error',
      refreshQuote: releaseAttempt && refreshableQuoteRejection(errors),
      releaseAttempt,
      retainedStatus: transaction
        ? transaction.state === 'successful'
          ? 'paid'
          : 'pending'
        : 'ambiguous',
      transaction,
    };
  }

  if (!transaction) {
    return { kind: 'ambiguous' };
  }

  if (transaction.state === 'failed') {
    return { message: transaction.message, kind: 'declined' };
  }

  if (transaction.state !== 'successful') {
    return { kind: 'pending', transaction };
  }

  return { kind: 'successful', transaction };
}

function checkoutRequestFailureMessage(quotedAttempt) {
  return quotedAttempt
    ? 'We could not confirm the payment result. Select Place Order again to safely check the same payment attempt.'
    : 'We could not confirm the legacy payment result. Do not place the order again until Check payment status confirms the existing attempt.';
}

function retainedOrderRecovery({ attempt, deliveryMethod, shippingAddress }) {
  if (attempt?.status !== 'paid') {
    return null;
  }

  if (!attempt.cartTransactionId) {
    return {
      error:
        'The retained payment identifier could not be loaded. Contact Retainer Club support and do not place the order again.',
    };
  }

  if (attempt.recoveryMode === 'quoted') {
    if (
      attempt.missingQuotedEvidence ||
      !attempt.taxQuoteId ||
      !attempt.shippingAddress ||
      !attempt.deliveryMethod
    ) {
      return {
        error:
          'The retained tax quote details could not be loaded. Contact Retainer Club support and do not place the order again.',
      };
    }

    return { input: attempt, newlyFrozen: false };
  }

  if (attempt.legacyValuesFrozen) {
    return { input: attempt, newlyFrozen: false };
  }

  return {
    input: {
      ...attempt,
      deliveryMethod,
      legacyValuesFrozen: true,
      shippingAddress,
    },
    newlyFrozen: true,
  };
}

function legacyCheckoutInput({
  deliveryMethod,
  idempotencyKey,
  shippingAddress,
}) {
  return {
    cartTransactionId: null,
    deliveryMethod,
    idempotencyKey,
    legacyValuesFrozen: true,
    missingQuotedEvidence: false,
    recovered: false,
    recoveryMode: 'legacy',
    shippingAddress,
    status: 'submitted',
    taxQuoteId: null,
  };
}

// "Plan 2 of 3" for the disclosure modal when a cart holds several plans.
function disclosurePosition(items, item) {
  if (!item) {
    return null;
  }

  return { index: items.indexOf(item) + 1, total: items.length };
}

function withFrozenLegacyValues(recoveredAttempt, retainedAttempt) {
  if (
    recoveredAttempt?.recoveryMode !== 'legacy' ||
    retainedAttempt?.recoveryMode !== 'legacy' ||
    retainedAttempt?.legacyValuesFrozen !== true
  ) {
    return recoveredAttempt;
  }

  return {
    ...recoveredAttempt,
    deliveryMethod: retainedAttempt.deliveryMethod,
    legacyValuesFrozen: true,
    shippingAddress: retainedAttempt.shippingAddress,
  };
}

export default function Example() {
  const [mutationErrors, setMutationErrors] = useState(null);
  const [isFitConfirmationOpen, setIsFitConfirmationOpen] = useState(false);
  const [hasAcceptedFitConfirmation, setHasAcceptedFitConfirmation] =
    useState(false);
  const [disclosureItem, setDisclosureItem] = useState(null);
  const [disclosureErrors, setDisclosureErrors] = useState(null);
  const [pendingCheckoutValues, setPendingCheckoutValues] = useState(null);
  const [requiresPaymentReconciliation, setRequiresPaymentReconciliation] =
    useState(false);
  const [retainedQuotedAttempt, setRetainedQuotedAttempt] = useState(null);
  const [shouldRefreshTaxQuote, setShouldRefreshTaxQuote] = useState(false);
  const [deliveryUpdateState, setDeliveryUpdateState] = useState({
    blocked: false,
    error: null,
    loading: false,
  });
  const idempotencyKeyRef = useRef(null);
  const quotedCheckoutInputRef = useRef(null);
  // Cart item ids the customer has acknowledged the subscription disclosure
  // for in this session. Every plan or subscription item needs one; payment
  // and order creation are refused server-side until they all exist.
  const acknowledgedItemIdsRef = useRef(new Set());
  const completedOrderNavigationRef = useRef(null);
  const navigate = useNavigate();

  const { account, accountError, accountLoading } = useAccount();
  const { cart, cartId, hasCartItems, updateCartLocal } = useCart();
  const { authLoading, currentUser } = useAuth();

  const {
    data: activeAttemptData,
    error: activeAttemptError,
    loading: activeAttemptQueryLoading,
    refetch: refetchActiveAttempt,
  } = useQuery(GET_ACTIVE_CHECKOUT_ATTEMPT, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !(cartId && account?.id),
    variables: { cartId },
  });
  const activeAttemptLoading =
    Boolean(cartId && account?.id) &&
    (activeAttemptQueryLoading || !(activeAttemptData || activeAttemptError));
  const discoveredCheckoutAttempt = useMemo(
    () => recoveredCheckoutAttempt(activeAttemptData?.activeCheckoutAttempt),
    [activeAttemptData?.activeCheckoutAttempt],
  );
  const checkoutAttempt = discoveredCheckoutAttempt?.orderId
    ? discoveredCheckoutAttempt
    : retainedQuotedAttempt || discoveredCheckoutAttempt || null;
  const completedOrderId = checkoutAttempt?.orderId || null;
  const allowEmptyCartRecovery = canFinishPaidQuotedAttempt(checkoutAttempt);
  const legacyRecoveryEditable =
    checkoutAttempt?.recovered === true &&
    checkoutAttempt?.recoveryMode === 'legacy' &&
    checkoutAttempt?.status === 'paid' &&
    checkoutAttempt?.legacyValuesFrozen !== true;
  const addressDeliveryLocked =
    Boolean(checkoutAttempt) && !legacyRecoveryEditable;

  const [create, { error, loading }] = useMutation(CREATE_ORDER);
  const [createCartTransaction, { error: ctError, loading: ctLoading }] =
    useMutation(CREATE_CART_TRANSACTION);
  const [createSubscriptionAcknowledgment, { loading: ackLoading }] =
    useMutation(CREATE_SUBSCRIPTION_ACKNOWLEDGMENT);

  useEffect(() => {
    if (!discoveredCheckoutAttempt || quotedCheckoutInputRef.current) {
      return;
    }

    quotedCheckoutInputRef.current = discoveredCheckoutAttempt;
    setRetainedQuotedAttempt(discoveredCheckoutAttempt);
    if (discoveredCheckoutAttempt.status === 'reconciliation_required') {
      setRequiresPaymentReconciliation(true);
      setMutationErrors(
        reconciliationErrors(discoveredCheckoutAttempt.message),
      );
    }
  }, [discoveredCheckoutAttempt]);

  useEffect(() => {
    if (
      !completedOrderId ||
      completedOrderNavigationRef.current === completedOrderId
    ) {
      return;
    }

    completedOrderNavigationRef.current = completedOrderId;
    navigate(`/checkout/success/${completedOrderId}`, {
      replace: true,
      state: { completedCartId: cartId },
    });
  }, [cartId, completedOrderId, navigate]);

  const matchedUser = useMemo(
    () => account?.users?.find((u) => u.email === currentUser?.email),
    [account?.users, currentUser?.email],
  );
  const memoizedInitialValues = useMemo(
    () => initialValues({ account, cartId, user: matchedUser }),
    [account, cartId, matchedUser],
  );

  const {
    register,
    handleSubmit,
    formState,
    control,
    getValues,
    setValue,
    getFieldState,
    ...rest
  } = useForm({
    resolver: yupResolver(
      schema({
        paymentRequired: !checkoutAttempt,
        shippingRequired: !addressDeliveryLocked,
      }),
    ),
    defaultValues: memoizedInitialValues,
    values: memoizedInitialValues,
    resetOptions: {
      keepDirtyValues: true,
    },
    mode: 'onBlur',
  });

  const shippingAddress = useWatch({ control, name: 'shippingAddress' });
  const deliveryMethod = useWatch({ control, name: 'deliveryMethod' });
  const addressValid = useMemo(() => {
    const addressId = shippingAddress?.id?.trim();
    if (addressId && addressId !== 'new-address') {
      return true;
    }

    return [
      shippingAddress?.address,
      shippingAddress?.city,
      shippingAddress?.country,
      shippingAddress?.postalCode,
      shippingAddress?.region,
    ].every((value) => typeof value === 'string' && value.trim().length > 0);
  }, [shippingAddress]);
  const taxQuote = useCheckoutTaxQuote({
    cart,
    cartId,
    shippingAddress,
    deliveryMethod,
    addressValid,
    updateCartLocal,
    paused:
      !account?.id ||
      Boolean(checkoutAttempt) ||
      activeAttemptLoading ||
      Boolean(activeAttemptError),
  });
  const taxCheckoutEnabled = taxQuote.capability?.enabled;
  const refreshTaxQuote = taxQuote.refresh;
  useEffect(() => {
    if (!shouldRefreshTaxQuote || retainedQuotedAttempt) {
      return;
    }

    setShouldRefreshTaxQuote(false);
    refreshTaxQuote();
  }, [refreshTaxQuote, retainedQuotedAttempt, shouldRefreshTaxQuote]);

  function transformCreditCard(creditCard) {
    if (creditCard.id === 'new-card') {
      return {
        nameOnCard: creditCard.nameOnCard,
        cardNumber: creditCard.cardNumber,
        expirationDate: creditCard.expirationDate,
        cvv: creditCard.cvv,
      };
    }

    if (creditCard.id) {
      return { id: creditCard.id };
    }

    return {
      nameOnCard: creditCard.nameOnCard,
      cardNumber: creditCard.cardNumber,
      expirationDate: creditCard.expirationDate,
      cvv: creditCard.cvv,
    };
  }

  function transformBillingAddress(billingAddress) {
    if (billingAddress.id) {
      return null;
    }

    return {
      address: billingAddress.address,
      address2: billingAddress.address2 || null,
      city: billingAddress.city,
      country: billingAddress.country,
      postalCode: billingAddress.postalCode,
      region: billingAddress.region,
    };
  }

  function transformShippingAddress(shippingAddress) {
    if (shippingAddress.id === 'new-address') {
      return {
        address: shippingAddress.address,
        address2: shippingAddress.address2 || null,
        city: shippingAddress.city,
        country: shippingAddress.country,
        postalCode: shippingAddress.postalCode,
        region: shippingAddress.region,
        isDefault: shippingAddress.isDefault,
      };
    }

    if (shippingAddress.id) {
      return {
        id: shippingAddress.id,
        address: shippingAddress.address || '',
        address2: shippingAddress.address2 || null,
        city: shippingAddress.city || '',
        country: shippingAddress.country || '',
        postalCode: shippingAddress.postalCode || '',
        region: shippingAddress.region || '',
      };
    }

    return {
      address: shippingAddress.address,
      address2: shippingAddress.address2 || null,
      city: shippingAddress.city,
      country: shippingAddress.country,
      postalCode: shippingAddress.postalCode,
      region: shippingAddress.region,
      isDefault: shippingAddress.isDefault,
    };
  }

  // If the server rejects for a missing acknowledgment, forget what was
  // acknowledged so the disclosure runs again on the next Place Order.
  function clearAcknowledgmentIfRejected(errors) {
    if (
      (errors || []).some(
        (errorMessage) =>
          errorMessage.code === SUBSCRIPTION_ACKNOWLEDGMENT_CODE,
      )
    ) {
      acknowledgedItemIdsRef.current = new Set();
    }
  }

  const disclosureItems = useMemo(() => disclosedCartItems(cart), [cart]);

  function nextUnacknowledgedItem() {
    return (
      disclosureItems.find(
        (item) => !acknowledgedItemIdsRef.current.has(item.id),
      ) || null
    );
  }

  function releaseQuotedCheckoutAttempt({ clearIdempotencyKey = false } = {}) {
    quotedCheckoutInputRef.current = null;
    setRetainedQuotedAttempt(null);
    if (clearIdempotencyKey) {
      idempotencyKeyRef.current = null;
    }
  }

  function updateRetainedAttemptStatus(status, transaction = null) {
    if (!quotedCheckoutInputRef.current) {
      return;
    }

    quotedCheckoutInputRef.current = {
      ...quotedCheckoutInputRef.current,
      cartTransactionId:
        transaction?.id ||
        quotedCheckoutInputRef.current.cartTransactionId ||
        null,
      message: transaction?.message || quotedCheckoutInputRef.current.message,
      status,
      taxQuoteId:
        transaction?.taxQuoteId ||
        quotedCheckoutInputRef.current.taxQuoteId ||
        null,
    };
    setRetainedQuotedAttempt(quotedCheckoutInputRef.current);
  }

  async function handleRecoveryRetry() {
    setMutationErrors(null);
    try {
      const result = await refetchActiveAttempt();
      const recoveredAttempt = withFrozenLegacyValues(
        recoveredCheckoutAttempt(result?.data?.activeCheckoutAttempt),
        quotedCheckoutInputRef.current,
      );
      if (!recoveredAttempt) {
        setMutationErrors([
          {
            path: 'payment',
            message:
              'No recoverable payment attempt was returned. Reload checkout before trying again.',
          },
        ]);
        return;
      }

      quotedCheckoutInputRef.current = recoveredAttempt;
      setRetainedQuotedAttempt(recoveredAttempt);
      const reconciliationRequired =
        recoveredAttempt.status === 'reconciliation_required';
      setRequiresPaymentReconciliation(reconciliationRequired);
      if (reconciliationRequired) {
        setMutationErrors(reconciliationErrors(recoveredAttempt.message));
      }
    } catch (_error) {
      setMutationErrors([
        {
          path: 'payment',
          message:
            'We could not refresh payment status. Checkout remains locked to prevent a duplicate charge.',
        },
      ]);
    }
  }

  function handleTransactionResponse(transactionData) {
    const outcome = classifyTransactionResponse(transactionData);

    if (outcome.kind === 'reconciliation') {
      updateRetainedAttemptStatus(
        'reconciliation_required',
        outcome.transaction,
      );
      setRequiresPaymentReconciliation(true);
      setMutationErrors(reconciliationErrors(outcome.transaction?.message));
      return null;
    }

    if (outcome.kind === 'error') {
      clearAcknowledgmentIfRejected(outcome.errors);
      if (outcome.releaseAttempt) {
        releaseQuotedCheckoutAttempt({ clearIdempotencyKey: true });
        if (outcome.refreshQuote) {
          setShouldRefreshTaxQuote(true);
        }
      } else {
        updateRetainedAttemptStatus(
          outcome.retainedStatus,
          outcome.transaction,
        );
      }
      setMutationErrors(outcome.refreshQuote ? null : outcome.errors);
      return null;
    }

    if (outcome.kind === 'ambiguous') {
      updateRetainedAttemptStatus('ambiguous');
      setMutationErrors([
        {
          path: 'payment',
          message:
            'We could not confirm the payment result. Select Place Order again to safely check the same payment attempt.',
        },
      ]);
      return null;
    }

    if (outcome.kind === 'declined') {
      releaseQuotedCheckoutAttempt({ clearIdempotencyKey: true });
      setMutationErrors([
        {
          path: 'payment',
          message:
            outcome.message ||
            'Your payment was declined. Check your payment details or use another card.',
        },
      ]);
      return null;
    }

    if (outcome.kind === 'pending') {
      updateRetainedAttemptStatus('pending', outcome.transaction);
      setMutationErrors([
        {
          path: 'payment',
          message:
            'Your payment is still processing. Use Check payment status before continuing; a new charge will not be created.',
        },
      ]);
      return null;
    }

    updateRetainedAttemptStatus('paid', outcome.transaction);
    return outcome.transaction;
  }

  async function submitOrder(
    shippingAddress,
    deliveryMethod,
    cartTransactionId,
    taxQuoteId,
  ) {
    try {
      const orderParams = {
        variables: {
          input: {
            input: {
              shippingAddress,
              deliveryMethod,
              cartId,
              cartTransactionId,
              ...(taxQuoteId ? { taxQuoteId } : {}),
            },
            clientMutationId: crypto.randomUUID(),
          },
        },
      };

      const { data } = await create(orderParams);

      const mErrors = data?.createOrder?.errorMessages || [];
      const order = data?.createOrder?.order;

      if (mErrors.length > 0) {
        clearAcknowledgmentIfRejected(mErrors);
        setMutationErrors(mErrors);
      } else if (!order?.id) {
        setMutationErrors([
          {
            path: 'order',
            message:
              'We could not finish creating your order. Select Place Order again to safely continue with the same payment.',
          },
        ]);
      } else {
        releaseQuotedCheckoutAttempt({ clearIdempotencyKey: true });
        setMutationErrors(null);
        navigate(`/checkout/success/${order.id}`, {
          replace: true,
          state: { completedCartId: cartId },
        });
      }
    } catch (_error) {
      setMutationErrors([
        {
          path: 'order',
          message:
            'We could not finish creating your order. Select Place Order again to safely continue with the same payment.',
        },
      ]);
    }
  }

  const onInvalid = useCallback((errors) => {
    const messages = Object.entries(errors).map(([path, error]) => ({
      path,
      message: error?.message || error?.root?.message || 'Invalid field',
    }));
    setMutationErrors(messages);
  }, []);

  async function submitPaymentAttempt(values, quotedCheckoutInput) {
    const { creditCard, billingAddress } = values;
    const transformedCreditCard = transformCreditCard(creditCard);
    const transformedBillingAddress = transformBillingAddress(billingAddress);
    const billingAddressId = billingAddress.id || null;

    try {
      const transactionParams = {
        variables: {
          input: {
            input: {
              cartId,
              creditCard: transformedCreditCard,
              billingAddress: transformedBillingAddress,
              billingAddressId,
              idempotencyKey:
                quotedCheckoutInputRef.current?.idempotencyKey || null,
              ...(quotedCheckoutInput
                ? {
                    deliveryMethod: quotedCheckoutInput.deliveryMethod,
                    shippingAddress: quotedCheckoutInput.shippingAddress,
                    taxQuoteId: quotedCheckoutInput.taxQuoteId,
                  }
                : {}),
            },
            clientMutationId: crypto.randomUUID(),
          },
        },
      };
      const { data: transactionData } =
        await createCartTransaction(transactionParams);
      const transaction = handleTransactionResponse(transactionData);

      if (!transaction) {
        return;
      }

      await submitOrder(
        quotedCheckoutInputRef.current?.shippingAddress,
        quotedCheckoutInputRef.current?.deliveryMethod,
        transaction.id,
        quotedCheckoutInputRef.current?.recoveryMode === 'quoted'
          ? transaction.taxQuoteId ||
              quotedCheckoutInputRef.current?.taxQuoteId ||
              null
          : null,
      );
    } catch (_error) {
      updateRetainedAttemptStatus('ambiguous');
      setMutationErrors([
        {
          path: 'checkout',
          message: checkoutRequestFailureMessage(Boolean(quotedCheckoutInput)),
        },
      ]);
    }
  }

  async function submitCheckout(values) {
    setMutationErrors(null);

    const blockingErrors = checkoutBlockingErrors({
      activeAttemptError,
      activeAttemptLoading,
      deliveryUpdateState,
      hasCartItems,
      retainedQuotedAttempt: checkoutAttempt,
      requiresPaymentReconciliation,
      taxQuote,
    });
    if (blockingErrors) {
      setMutationErrors(blockingErrors);
      return;
    }

    const { shippingAddress, deliveryMethod } = values;
    const transformedShippingAddress =
      transformShippingAddress(shippingAddress);

    const recovery = retainedOrderRecovery({
      attempt: checkoutAttempt,
      deliveryMethod,
      shippingAddress: transformedShippingAddress,
    });
    if (recovery?.error) {
      setMutationErrors([{ path: 'payment', message: recovery.error }]);
      return;
    }

    if (recovery?.input) {
      const retainedOrderInput = recovery.input;
      if (recovery.newlyFrozen) {
        quotedCheckoutInputRef.current = retainedOrderInput;
        setRetainedQuotedAttempt(retainedOrderInput);
      }

      await submitOrder(
        retainedOrderInput.shippingAddress,
        retainedOrderInput.deliveryMethod,
        retainedOrderInput.cartTransactionId,
        retainedOrderInput.recoveryMode === 'quoted'
          ? retainedOrderInput.taxQuoteId
          : null,
      );
      return;
    }

    const hadRetainedQuotedAttempt = Boolean(quotedCheckoutInputRef.current);
    const quotedCheckoutInput = prepareQuotedCheckoutInput({
      deliveryMethod,
      idempotencyKeyRef,
      quotedCheckoutInputRef,
      shippingAddress: transformedShippingAddress,
      setRetainedQuotedAttempt,
      taxCheckoutEnabled,
      taxQuoteId: taxQuote.quoteId,
    });
    if (quotedCheckoutInput && !hadRetainedQuotedAttempt) {
      taxQuote.invalidate();
    }

    if (!quotedCheckoutInput) {
      if (!idempotencyKeyRef.current) {
        idempotencyKeyRef.current = createCheckoutIdempotencyKey();
      }
      quotedCheckoutInputRef.current = legacyCheckoutInput({
        deliveryMethod,
        idempotencyKey: idempotencyKeyRef.current,
        shippingAddress: transformedShippingAddress,
      });
      setRetainedQuotedAttempt(quotedCheckoutInputRef.current);
    }

    await submitPaymentAttempt(values, quotedCheckoutInput);
  }

  async function continueAfterDisclosure(values) {
    if (requiresFitConfirmation(cart) && !hasAcceptedFitConfirmation) {
      setPendingCheckoutValues(values);
      setIsFitConfirmationOpen(true);
      return;
    }

    await submitCheckout(values);
  }

  async function submitRecord(values) {
    const nextItem = nextUnacknowledgedItem();
    if (nextItem) {
      setPendingCheckoutValues(values);
      setDisclosureErrors(null);
      setDisclosureItem(nextItem);
      return;
    }

    await continueAfterDisclosure(values);
  }

  async function handleDisclosureConfirm(submission) {
    setDisclosureErrors(null);

    try {
      const { itemId, ...rest } = submission;
      const { data } = await createSubscriptionAcknowledgment({
        variables: {
          input: {
            input: { cartItemId: itemId, ...rest },
            clientMutationId: crypto.randomUUID(),
          },
        },
      });

      const payload = data?.createSubscriptionAcknowledgment;
      const errors = payload?.errorMessages || [];
      if (errors.length > 0 || !payload?.accountEvent?.id) {
        setDisclosureErrors(
          errors.length > 0
            ? errors
            : [
                {
                  path: 'base',
                  message:
                    'We could not save your acknowledgment. Please try again.',
                },
              ],
        );
        return;
      }

      acknowledgedItemIdsRef.current.add(itemId);

      const nextItem = nextUnacknowledgedItem();
      if (nextItem) {
        setDisclosureItem(nextItem);
        return;
      }

      setDisclosureItem(null);
      await continueAfterDisclosure(pendingCheckoutValues || getValues());
    } catch (_error) {
      setDisclosureErrors([
        {
          path: 'base',
          message: 'We could not save your acknowledgment. Please try again.',
        },
      ]);
    }
  }

  function handleDisclosureBack() {
    setDisclosureItem(null);
    setDisclosureErrors(null);
  }

  async function handleFitConfirmationPlaceOrder() {
    setHasAcceptedFitConfirmation(true);
    setIsFitConfirmationOpen(false);

    await submitCheckout(pendingCheckoutValues || getValues());
  }

  function handleFitConfirmationBack() {
    setIsFitConfirmationOpen(false);
  }

  if (authLoading || accountLoading) {
    return <BaseLoader title="Checkout" />;
  }

  if (!currentUser) {
    return (
      <div className="mx-auto mt-8 max-w-2xl rounded-md bg-red-50 p-4 text-red-800">
        Sign in before continuing to checkout.
      </div>
    );
  }

  if (accountError || !account) {
    return (
      <div className="mx-auto mt-8 max-w-2xl rounded-md bg-red-50 p-4 text-red-800">
        Your customer account is not available. Contact Retainer Club support
        before continuing to checkout.
      </div>
    );
  }

  return (
    <div className="mb-24">
      <div className="mx-auto max-w-7xl gap-y-4 sm:pt-4">
        <div className="mx-auto max-w-2xl px-4 pt-8 pb-24 sm:px-6 lg:max-w-7xl lg:px-8">
          <FormProvider
            {...rest}
            control={control}
            register={register}
            handleSubmit={handleSubmit}
            formState={formState}
            getValues={getValues}
            setValue={setValue}
            getFieldState={getFieldState}
          >
            <form
              className="lg:grid lg:grid-cols-2 lg:gap-x-12 xl:gap-x-16"
              onSubmit={handleSubmit(submitRecord, onInvalid)}
              noValidate
            >
              <CheckoutForm
                addressDeliveryLocked={addressDeliveryLocked}
                taxCheckoutEnabled={taxCheckoutEnabled}
                deliveryUpdateState={deliveryUpdateState}
                onDeliveryUpdateStateChange={setDeliveryUpdateState}
                retainedAttempt={checkoutAttempt}
              />

              <CheckoutCart
                allowEmptyCartRecovery={allowEmptyCartRecovery}
                commercialInputsLocked={
                  Boolean(checkoutAttempt) ||
                  activeAttemptLoading ||
                  Boolean(activeAttemptError)
                }
                error={error}
                mutationErrors={mutationErrors}
                loading={loading}
                ctError={ctError}
                ctLoading={ctLoading}
                taxQuote={taxQuote}
                retainedAttempt={checkoutAttempt}
                recoveryError={activeAttemptError}
                recoveryLoading={
                  activeAttemptLoading || Boolean(completedOrderId)
                }
                onRetryRecovery={handleRecoveryRetry}
                requiresPaymentReconciliation={requiresPaymentReconciliation}
                deliveryUpdateState={deliveryUpdateState}
              />
            </form>
          </FormProvider>
          <SubscriptionDisclosureModal
            key={disclosureItem?.id}
            open={Boolean(disclosureItem)}
            item={disclosureItem}
            onBack={handleDisclosureBack}
            onConfirm={handleDisclosureConfirm}
            isSubmitting={ackLoading}
            errorMessages={disclosureErrors}
            position={disclosurePosition(disclosureItems, disclosureItem)}
          />
          <CheckoutFitConfirmationModal
            open={isFitConfirmationOpen}
            onBack={handleFitConfirmationBack}
            onPlaceOrder={handleFitConfirmationPlaceOrder}
            isSubmitting={loading || ctLoading}
          />
        </div>
      </div>
    </div>
  );
}
